import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSettings, REMINDER_MODES } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import { useVoice } from '../context/VoiceContext';
import { api } from '../utils/api';
import InfoTip from '../components/InfoTip';
import DeviceCapabilityModal from '../components/DeviceCapabilityModal';
import { getDeviceSummary } from '../utils/deviceCapability';
import { playAlertBeep } from '../utils/alertSound';

const THEME_COLORS = [
  { name: '健康绿', value: '#4CAF50', color: '#4CAF50' },
  { name: '温暖橙', value: '#FF8C42', color: '#FF8C42' },
  { name: '宁静蓝', value: '#2196F3', color: '#2196F3' },
  { name: '优雅紫', value: '#9C27B0', color: '#9C27B0' },
];

const FONT_SIZES = [
  { name: '标准', value: 'medium' },
  { name: '大号', value: 'large' },
  { name: '超大号', value: 'xlarge' },
];

/** 卡片内的分组标题。
 *  这里刻意**不画线**：上面那一项（界面操作播报）自带 1px 下边框，
 *  分组标题若再画 1px 上边框，两条线会在 22px 内平行出现 —— 看起来就是
 *  「两条多余的杠」（用户实拍反馈「中间的两条杠删掉」）。
 *  分组感改由「更大的上间距 + 小标题自身的字重」承担；
 *  而且这两项本来就是同一类（都跟"出声"有关），本就不该被横线切开。 */
const groupHeadStyle = {
  marginTop: 22,
  paddingTop: 16,
};

/** 分组标题和右侧说明气泡同行：标题不折行，气泡不把标题挤走 */
const reminderHeadStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

export default function Settings() {
  const { settings, updateSettings } = useSettings();
  const { user, logout } = useAuth();
  const { speak } = useVoice();
  const navigate = useNavigate();
  const location = useLocation();
  const [badges, setBadges] = useState([]);
  // 从首页「我的徽章 → 全部 ›」过来时地址带 #badges，直接展开，省掉一次多余点击
  const [showBadges, setShowBadges] = useState(() => location.hash === '#badges');
  const badgeCardRef = React.useRef(null);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  // 本机支持哪些提醒手段：进页面检测一次，直接告诉用户，而不是让他点了没反应
  const [device] = useState(() => getDeviceSummary());
  // 点「✗」那格时弹出来的说明（值是能力 key，null 表示不弹）
  const [capability, setCapability] = useState(null);

  const loadBadges = () => {
    api.getBadges().then(setBadges).catch(() => {});
  };

  React.useEffect(() => { if (showBadges) loadBadges(); }, [showBadges]);

  /**
   * 带 #badges 进来时，滚到徽章卡片。
   *
   * 为什么要延时：徽章墙是展开后才渲染的，如果立刻 scrollIntoView，
   * 页面高度还是展开前的，滚到的位置会偏掉一大截。
   * 等一帧让展开后的布局落定再滚（80ms 是量出来的安全余量，比 requestAnimationFrame
   * 稳，因为徽章卡片里还有图片字体等异步影响高度的东西）。
   */
  React.useEffect(() => {
    if (location.hash !== '#badges') return undefined;
    setShowBadges(true);
    const t = setTimeout(() => {
      if (badgeCardRef.current) {
        badgeCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 80);
    return () => clearTimeout(t);
  }, [location.hash]);

  const handleChangePassword = async () => {
    setPasswordMsg('');
    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordMsg('请填写完整');
      setPasswordError(true);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg('两次输入的新密码不一致');
      setPasswordError(true);
      return;
    }
    if (newPassword.length < 4) {
      setPasswordMsg('新密码至少4位');
      setPasswordError(true);
      return;
    }
    try {
      await api.changePassword({ oldPassword, newPassword });
      setPasswordMsg('密码修改成功');
      setPasswordError(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      speak('密码修改成功');
    } catch (err) {
      setPasswordMsg(err.message || '修改失败');
      setPasswordError(true);
    }
  };

  const handleChange = (key, value) => {
    updateSettings({ [key]: value });
  };

  const handleLogout = async () => {
    if (!window.confirm('确定要退出登录吗？')) return;
    await logout();
    navigate('/login');
  };

  const handleClearData = async () => {
    if (!window.confirm('确定要清除所有数据吗？此操作不可恢复！')) return;
    if (!window.confirm('再次确认：所有药品、提醒和记录都将被删除！')) return;
    try {
      await api.clearAllData();
      speak('所有数据已清除');
      alert('所有数据已清除');
      window.location.reload();
    } catch (err) {
      alert('清除失败：' + err.message);
    }
  };

  return (
    <div className="app-container">
      <h1 className="page-title">设置</h1>

      {/*
        卡片顺序（用户指定的优先级）：
          主题设置 → 字体大小 → 语音与提醒 → 应用名称 → 个人信息 → 徽章 → 账号 → 数据 → 关于
        主题和字体是老人第一眼最可能要改的两项，所以提到最前；
        「应用名称」原来是第一张卡（要输入，属于配置类），下移到语音之后。
      */}

      {/* 主题设置：颜色 4 档 + 夜间模式，压在同一行点选 */}
      <div className="card">
        <div className="card-title">🎨 主题设置</div>
        {/* 原来是两行：上面 4 个色球（每个 56px 圆 + 名字），
            下面「🌙 夜间模式 / 深色背景，夜间使用更护眼 / 开关」。
            夜间模式那句说明只是把按钮名字换个说法，删掉不影响能不能用 ——
            点一下就知道会变成什么样。开关随之改成第 5 个格子，整组从两行变一行。
            夜间那一格关闭时是空心月亮、开启时是深底亮月，
            不靠颜色也能看出状态，色盲用户一样分得清。 */}
        <div className="theme-row">
          {THEME_COLORS.map(c => {
            const selected = settings.theme_color === c.value;
            return (
              <button
                key={c.value}
                type="button"
                className={`theme-cell ${selected ? 'selected' : ''}`}
                aria-pressed={selected}
                onClick={() => { handleChange('theme_color', c.value); speak(`已切换到${c.name}主题`); }}
              >
                <span className="theme-dot" style={{ background: c.color }}>
                  {selected ? '✓' : ''}
                </span>
                <span className="theme-cell-label">{c.name}</span>
              </button>
            );
          })}
          <button
            type="button"
            className={`theme-cell ${settings.dark_mode ? 'selected' : ''}`}
            aria-pressed={!!settings.dark_mode}
            onClick={() => {
              const next = !settings.dark_mode;
              handleChange('dark_mode', next);
              speak(next ? '已开启夜间模式' : '已关闭夜间模式');
            }}
          >
            <span className={`theme-dot theme-dot-night ${settings.dark_mode ? 'on' : ''}`}>🌙</span>
            <span className="theme-cell-label">夜间</span>
          </button>
        </div>
      </div>

      {/* 字体大小：去掉「调整界面文字大小，方便阅读」这句——
          标题本身就写着「字体大小」，再解释一遍是同一句话说两次 */}
      <div className="card">
        <div className="card-title">🔤 字体大小</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {FONT_SIZES.map(f => (
            <button
              key={f.value}
              className={settings.font_size === f.value ? 'btn-primary' : 'btn-outline'}
              /* 三个按钮等分一行。原本用 .btn-outline 的 16px×24px 内边距，
                 超大号字号下「超大号」三个字 84px + 内边距 48px = 132px，
                 而按钮只有约 103px 宽，文字直接顶出按钮边框。
                 这里把内边距压到 4px、字号降到 small，并允许折行兜底。 */
              style={{
                flex: 1,
                minWidth: 0,
                minHeight: 56,
                padding: '12px 4px',
                fontSize: 'var(--font-size-small)',
                whiteSpace: 'normal',
                lineHeight: 1.25,
              }}
              onClick={() => { handleChange('font_size', f.value); speak(`字体大小已调整为${f.name}`); }}
            >
              {f.name}
            </button>
          ))}
        </div>
      </div>

      {/*
        语音与提醒设置
        原「提醒设置」与「语音设置」两张卡已合并为一张 —— 它们本来就是同一件事
        （声音），拆成两处反而让用户要在两个地方找「声音」开关。
      */}
      <div className="card">
        <div className="card-title">🔊 语音与提醒</div>

        {/* 界面操作播报 + 右侧的「？」（用户要求：问号跟在这一项右边）。
            原来这里还有一层名为「语音播报」的分组小标题，下面只挂这一个开关 ——
            小标题和开关说的是同一件事，而它的气泡里解释的又正是「界面操作播报」，
            标题与内容对不上。删掉小标题，解释直接折进这一项的问号。 */}
        {/* 这一项不画下边框（原来有）：它下面紧接「到点提醒方式」的分组标题，
            而那个标题自己也曾画着上边框 —— 两条 1px 细线隔 22px 并排，
            看着就是两条没用的杠。现在两边都不画。 */}
        <div className="setting-item" style={{ borderBottom: 'none' }}>
          <div>
            <div className="setting-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              👆 界面操作播报
              <InfoTip title="这个开关管什么？">
                只在你点了某个按钮之后才出声，说的是操作结果（例如切换主题后念「已切换到温暖橙主题」）。
                跟吃药时间无关，嫌吵可以关掉，不影响吃药提醒。
              </InfoTip>
            </div>
            <div className="setting-desc">点了按钮，念一句结果</div>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={!!settings.voice_ui}
              onChange={e => {
                handleChange('voice_ui', e.target.checked);
                speak(e.target.checked ? '界面操作播报已开启' : '界面操作播报已关闭', { force: e.target.checked });
              }}
            />
            <span className="slider" />
          </label>
        </div>

        {/* —— 分组二：到点提醒方式 ——
            标题改用 .setting-label（字号与加粗跟「界面操作播报」「语音速度」同级），
            并给它配了自己的图标 —— 这样它在卡片里读起来是一个**小标题**。
            原来用的是 .section-head-text（small 字号 + 常规字重 + 灰色），
            摆在 5 个选项格子上面，像给格子写的一行注解，层级全丢了：
            用户扫过去不知道这是"一整组设置的名字"还是"格子下面的补充说明"。
            选项仍是 5 个等分格子 + 2 字短标签（见 SettingsContext 的 short 字段；
            写 4 个字在超大号字号下会顶破格子），解释折进右侧气泡。 */}
        <div style={groupHeadStyle}>
          <div style={{ ...reminderHeadStyle, marginBottom: 10 }}>
            <span className="setting-label">🔔 到点提醒方式</span>
            <InfoTip title="这五种分别是什么意思？">
              <b>全部</b>：语音、铃声、振动三种一起来，最不容易错过。<br />
              <b>语音</b>：只用说话的方式提醒，安静环境也听得见。<br />
              <b>铃声</b>：只响提示音，不说话。<br />
              <b>振动</b>：只让手机振动（iPhone 的浏览器不支持这一项）。<br />
              <b>静音</b>：不发声也不振动，只在屏幕上弹提醒。
            </InfoTip>
          </div>

          <div className="reminder-mode-row">
            {REMINDER_MODES.map(m => {
              const active = settings.reminder_mode === m.value;
              // 这台手机在这档里能真正做到什么，直接说；不让用户选了却永远等不到
              const unavailable = m.value === 'vibrate' && !device.vibrate;
              return (
                <button
                  key={m.value}
                  type="button"
                  aria-pressed={active}
                  className={`reminder-mode-chip ${active ? 'active' : ''} ${unavailable ? 'off' : ''}`}
                  onClick={() => {
                    // 点不了的那一项不静默失败：直接说清为什么，别让用户反复戳
                    if (unavailable) {
                      speak('这台手机不支持振动提醒，请换一种方式', { force: true });
                      return;
                    }
                    handleChange('reminder_mode', m.value);
                    // 选完立刻让用户听到 / 感受到这一档的实际效果
                    if (m.value === 'all' || m.value === 'voice') {
                      speak(`提醒方式已改为${m.label}`, { force: true });
                    } else if (m.value === 'sound') {
                      playAlertBeep(2);
                    } else if (m.value === 'vibrate') {
                      try { navigator.vibrate([200, 100, 200]); } catch (e) {}
                    }
                  }}
                >
                  {m.short}
                </button>
              );
            })}
          </div>
        </div>

        <div className="setting-item">
          <div>
            <div className="setting-label">🔄 重复提醒</div>
            <div className="setting-desc">没确认的话，5 分钟后再喊一次</div>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={!!settings.reminder_repeat}
              onChange={e => {
                handleChange('reminder_repeat', e.target.checked);
                speak(e.target.checked ? '重复提醒已开启' : '重复提醒已关闭');
              }}
            />
            <span className="slider" />
          </label>
        </div>

        {/* 语音速度放在「重复提醒」下面（用户要求）。
            它跟的是「出声」这一串：先定怎么提醒、要不要多喊几遍，最后才调快慢；
            原来夹在「界面操作播报」和「到点提醒方式」中间，
            把两个同类开关（一个是 UI 播报，一个是到点提醒）硬生生切开了。 */}
        <div className="setting-item" style={{ marginTop: 16 }}>
          <div>
            <div className="setting-label">⏱️ 语音速度</div>
            <div className="setting-desc">界面和提醒的播报，都能调快慢</div>
          </div>
          <select
            className="input-field"
            style={{ width: 'auto', minWidth: 110, maxWidth: '45%', padding: '10px 12px', minHeight: 44 }}
            value={settings.voice_speed || '1'}
            onChange={e => {
              handleChange('voice_speed', e.target.value);
              speak('语音速度已调整');
            }}
          >
            <option value="0.7">较慢</option>
            <option value="1">正常</option>
            <option value="1.3">较快</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
          <button
            className="btn-outline"
            style={{ flex: 1, width: 'auto', minHeight: 48, whiteSpace: 'nowrap' }}
            onClick={() => speak('语音测试，如果您能听到这句话，说明语音播报功能正常', { force: true })}
          >
            🔊 测试语音
          </button>
          <InfoTip title="关于「测试语音」" buttonLabel="关于测试语音">
            测试语音不受上面的开关影响，用来确认手机音量和语音引擎是否正常。
          </InfoTip>
        </div>

        {/* 本机支持情况。
            原来只是 4 个裸标签「语音播报 ✓ 提示铃声 ✓ 振动 ✗ 语音录入 ✓」，
            用户反馈「看不太懂」—— 不知道这几个字是**这台设备**的检查结果，
            还是软件的介绍 / 是"本软件支持这些功能"的意思。所以加一句前缀点名主体。
            前缀用「本设备」而不是「本手机」：这一页也可能是在电脑浏览器上打开的
            （2026-09-22 用户反馈），说成手机就与事实不符。
            另外 ✗ 那一格原来是个死标签，点不动也没解释：用户只看到一个红叉，
            既不知道怪谁（设备？软件坏了？网络？），也不知道下一步干什么。
            现在 ✗ 变成可点按钮，点开一次性说清「什么情况 / 为什么 / 怎么办」。 */}
        <div className="setting-label" style={{ marginTop: 18 }}>📱 本设备支持</div>
        <div className="device-strip">
          {[
            { key: 'speech', on: device.speech, name: '语音播报' },
            { key: 'sound', on: device.sound, name: '提示铃声' },
            { key: 'vibrate', on: device.vibrate, name: '振动' },
            { key: 'recognition', on: device.recognition, name: '语音录入' },
          ].map(c => (
            c.on ? (
              <span key={c.name} className="device-chip">
                {c.name} ✓
              </span>
            ) : (
              <button
                key={c.name}
                type="button"
                className="device-chip off"
                onClick={() => setCapability(c.key)}
                aria-label={`${c.name}不可用，查看原因`}
              >
                {c.name} ✗
              </button>
            )
          ))}
        </div>
        {(!device.speech || !device.sound || !device.vibrate || !device.recognition) && (
          <div className="device-support-note">
            带 ✗ 的项，点一下就知道是什么原因、还能怎么办
          </div>
        )}
        <div className="device-support-note">
          提醒只在<b>本页面开着</b>时才会响；锁屏或切到别的应用不会提醒
        </div>
      </div>

      <DeviceCapabilityModal capability={capability} onClose={() => setCapability(null)} />

      {/* 应用名称（用户要求：放在「语音与提醒」之后、「个人信息」之前）。
          原来是第一张卡 —— 但它属于「配置一次就不动」的项，
          排在主题、字体这种「天天可能要调」的前面不合顺序。 */}
      <div className="card">
        <div className="card-title">🏷️ 应用名称</div>
        <div className="card-subtitle">给您的应用起个名字，会显示在标题栏</div>
        <input
          className="input-field"
          value={settings.app_name}
          onChange={e => handleChange('app_name', e.target.value)}
          placeholder="如：吃药提醒"
        />
      </div>

      {/* 个人信息 */}
      <div className="card">
        <div className="card-title">👤 个人信息</div>
        <div className="form-group">
          <label className="form-label">您的称呼（用于个性化播报）</label>
          <input
            className="input-field"
            value={settings.user_name || ''}
            onChange={e => handleChange('user_name', e.target.value)}
            placeholder="如：王爷爷"
          />
        </div>
        <div className="form-group">
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            家人联系方式（选填）
            {/* 这段说明和下面输入框的 placeholder 说的是同一件事，占两行纯属重复 —— 折进气泡 */}
            <InfoTip title="怎么写？">
              写清「称呼 + 电话号码」，例如「儿子 王小明 13800000000」。
              首页会出现「通知家人」按钮，一键拨过去。
            </InfoTip>
          </label>
          <input
            className="input-field"
            type="tel"
            value={settings.emergency_contact_1 || ''}
            onChange={e => handleChange('emergency_contact_1', e.target.value)}
            placeholder="如：儿子 王小明 13800000000"
            style={{ marginBottom: 8 }}
          />
          <input
            className="input-field"
            type="tel"
            value={settings.emergency_contact_2 || ''}
            onChange={e => handleChange('emergency_contact_2', e.target.value)}
            placeholder="如：女儿 王小红 13900000000（可不填）"
          />
          {/* 这条边界必须写在界面上，不能只写在文档里。
              但也不必占三行：把「身体出现紧急情况」压成「紧急」、
              去掉重复的「拨打」，核心信息（只联系家人 / 不是急救 / 拨 120）一字不少。 */}
          <div className="ai-disclaimer" style={{ marginTop: 12 }}>
            只联系家人，不是急救通道；紧急拨 120
          </div>
        </div>
      </div>

      {/* 徽章展示。
          id="badges" + ref 是首页「全部 ›」的落点：地址带 #badges 进来时，
          这一段会**自动展开**并滚到可视区（见组件上方的 location.hash 处理）。
          卡片上部留 12px 的 scroll-margin，滚动定位时不会贴着屏幕顶边。 */}
      <div className="card" id="badges" ref={badgeCardRef}>
        <div className="card-title">🏅 我的徽章</div>
        <button
          className="btn-outline"
          style={{ minHeight: 56 }}
          onClick={() => setShowBadges(!showBadges)}
        >
          {showBadges ? '收起徽章' : '查看我的徽章'}
        </button>
        {showBadges && (
          <div className="badge-grid">
            {badges.map(b => (
              <div key={b.id} className={`badge-item ${b.earned ? 'earned' : 'badge-locked'}`}>
                <div className="badge-icon">{b.icon}</div>
                <div className="badge-name">{b.name}</div>
                {!b.earned && (
                  <div style={{ fontSize: 'var(--font-size-small)', color: 'var(--text-light)', marginTop: 4 }}>
                    {b.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 账号 */}
      <div className="card">
        <div className="card-title">👤 账号</div>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 12 }}>
          {user ? `已登录：${user.username}` : '未登录'}
        </p>

        {/* 修改密码 */}
        <div style={{ marginBottom: 12 }}>
          <div className="card-subtitle">修改密码</div>
          <input
            className="input-field"
            type="password"
            placeholder="原密码"
            value={oldPassword}
            onChange={e => setOldPassword(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <input
            className="input-field"
            type="password"
            placeholder="新密码（至少4位）"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <input
            className="input-field"
            type="password"
            placeholder="确认新密码"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <button
            className="btn-outline"
            style={{ minHeight: 48, width: '100%' }}
            onClick={handleChangePassword}
          >
            修改密码
          </button>
          {passwordMsg && (
            <p style={{ marginTop: 8, color: passwordError ? '#C62828' : '#2E7D32', fontSize: 'var(--font-size-small)' }}>
              {passwordMsg}
            </p>
          )}
        </div>

        <button
          className="btn-danger"
          style={{ minHeight: 48 }}
          onClick={handleLogout}
        >
          退出登录
        </button>
      </div>

      {/* 数据管理 */}
      <div className="card">
        <div className="card-title">🗑️ 数据管理</div>
        <div className="card-subtitle">
          清除当前账号下的所有药品、提醒和用药记录，此操作不可恢复。
        </div>
        <button
          className="btn-danger"
          style={{ minHeight: 48 }}
          onClick={handleClearData}
        >
          🗑️ 清除所有数据
        </button>
      </div>

      {/* 关于 */}
      <div className="card" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>💊</div>
        <div style={{ fontSize: 'var(--font-size-large)', fontWeight: 700, marginBottom: 4 }}>
          {settings.app_name} v1.0
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-small)' }}>
          关爱老年人健康，贴心用药提醒
        </p>
        <div className="ai-disclaimer" style={{ marginTop: 12 }}>
          识别与核对仅供参考，具体用药请遵医嘱
        </div>
      </div>
    </div>
  );
}
